import _ from 'lodash';
import moment from 'moment';
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Button, Badge, FormControl, FormCheck, Alert, Tabs, Tab, FormGroup } from 'react-bootstrap';
import classNames from 'classnames';
import ApiService from '../../services/ApiService';
import ModalTable from '../modal/ModalTable';
import LoadingPage from '../LoadingPage';
import CurrencyItem from '../form/misc/CurrencyItem';
import QuotePostLine from './qpl/QuotePostLine';
import QuotePostLinePrimary from './qpl/QuotePostLinePrimary';
import QuotePostLineSecondary from './qpl/QuotePostLineSecondary';
import FormInputListSearch from '../form/control/FormInputListSearch';
import { getListItemId } from '../../util/form';
import { statusColors, getStatus } from '../../util/vehicle';
import { modal } from '../modal/ModalEmitter';
import toastr from '../toastr';
import { glCodeFieldName } from './util';
import PostDealConfirmation from './PostDealConfirmation';
import SimpleQuoteBadge from './SimpleQuoteBadge';

const headers = [
  { text: 'FieldName', hide: true },
  { text: 'Field' },
  { text: 'GL Account' },
  { text: 'Control' },
  { text: 'Reference', hide: true },
  { text: 'UnitId' },
  { text: 'Value', type: 'currencyColumn', className: 'right' },
];

const aggHeaders = [
  { text: 'FieldName', hide: true },
  { text: 'Field' },
  { text: 'GL Account' },
  { text: 'Source Account' },
  { text: 'Control' },
  { text: 'Value', type: 'currencyColumn', className: 'right' },
];

const PostDealToAccounting = ({ dealId, refreshDeal }) => {
  const [activeTabIndex, setActiveTabIndex] = useState('notready');
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [checkingCost, setCheckingCost] = useState(false);
  const [deal, setDeal] = useState({});
  const [quoteList, setQuoteList] = useState([]);
  const [selected, setSelected] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [postingData, setPostingData] = useState({});
  const [postingTotal, setPostingTotal] = useState();
  const [postingSource, setPostingSource] = useState({});
  const [postingAddendum, setPostingAddendum] = useState({});
  const [accounts, setAccounts] = useState([]); // For Account Lookup

  const [excludeZeros, setExcludeZeros] = useState(true);
  const [expandedQuotes, setExpandedQuotes] = useState([]);

  const [accountMappings, setAccountMappings] = useState([]);
  const [fieldLookup, setFieldLookup] = useState([]);

  const [mappingValues, setMappingValues] = useState({});
  const [notReadyErrors, setNotReadyErrors] = useState({});

  const user = useSelector(state => state.app.user);

  const [postingBranch, setPostingBranch] = useState(user.branch.id);

  const quotesThatAreNotReady = () => {
    return quoteList.filter(quote => {
      if (
        quote.QuoteState.logicalName === 'BOOKED' ||
        quote.QuoteState.logicalName === 'POSTED' ||
        quote.QuoteState.logicalName === 'CANCELLED' ||
        quote.QuoteState.logicalName === 'CLOSED'
      ) {
        return false;
      }

      return true;
    });
  };

  const quotesThatAreReady = () => {
    return quoteList.filter(quote => {
      if (quote.QuoteState.logicalName !== 'BOOKED') {
        return false;
      }

      return true;
    });
  };

  const quotesAlreadyPosted = () => {
    return quoteList.filter(quote => {
      if (quote.QuoteState.logicalName !== 'POSTED' && quote.QuoteState.logicalName !== 'CLOSED') {
        return false;
      }

      return true;
    });
  };

  const handlePostingSourceSelected = (value, quoteId) => {
    setPostingSource(prev => ({
      ...prev,
      [quoteId]: getListItemId(value),
    }));
  };

  const handlePostingSourceChange = (value, quoteId) => {
    // only checking for clear
    if (value === '') {
      setPostingSource(prev => ({
        ...prev,
        [quoteId]: getListItemId(value),
      }));
    }
  };

  const handleExpandAll = () => {
    const all = [...quotesThatAreReady().map(q => q.id), 'agg'];

    // Collapse All
    if (
      _.isEqual(
        expandedQuotes,
        all
      )
    ) {
      setExpandedQuotes([]);
      return;
    }

    // Expand All
    setExpandedQuotes(all);
  };

  const handleCurrentBranchChange = event => {
    setPostingBranch(Number(event.target.value));
  };

  const handleCloseAll = () => {
    return ApiService.put('crm/quotes/batch-close', { quotes: quotesAlreadyPosted().map(q => q.id) }).then(fetchDeal);
  };

  const handleRevertPosted = () => {
    return ApiService.put('crm/quotes/batch-book', { quotes: quotesAlreadyPosted().map(q => q.id) }).then(fetchDeal);
  };

  const handleBookAll = () => {
    return bookQuotes(quotesThatAreNotReady().map(q => q.id));
  };

  const handleBookSelected = () => {
    return bookQuotes(selected);
  };

  const handleExpand = quoteId => {
    // Collapse
    if (expandedQuotes.includes(quoteId)) {
      setExpandedQuotes(current => current.filter(q => q !== quoteId));
      return;
    }

    // Expand
    setExpandedQuotes(current => [...current, quoteId]);
  };

  const bookQuotes = quotes => {
    setBooking(true);
    setNotReadyErrors({});
    return ApiService.put('crm/quotes/batch-book', { quotes })
      .then(response => {
        const errors = _.get(response, 'data.errors');

        setNotReadyErrors(errors || {});
        setSelected([]);
        fetchDeal();
      })
      .catch(error => {
        toastr.error('Error', error.message);
      })
      .finally(() => {
        setBooking(false);
      });
  };

  const handlePostAll = () => {
    const sources = {};
    const ready = [];
    const notready = [];

    // Get posting date if they are all equal
    let titleDatesEqual = false;
    let titleDate;

    quotesThatAreReady().forEach(quote => {
      const mappingEntry = accountMappings.find(o => o.id === mappingValues[quote.id]);
      if (postingData[quote.id].length && postingTotal[quote.id] === 0 && mappingEntry) {
        ready.push(postingData[quote.id]);
      } else {
        notready.push(postingData[quote.id]);
        return;
      }

      if (!sources[mappingEntry.source]) {
        sources[mappingEntry.source] = [];
      }

      const quoteTitleDate = _.get(quote, 'data.documentTitleDate');

      if (titleDate && quoteTitleDate !== titleDate) {
        titleDatesEqual = false;
      }

      if (!titleDate) {
        titleDatesEqual = true;
        titleDate = quoteTitleDate;
      }

      const filteredPostData = postingData[quote.id].filter(entry => {
        // Position 8 isAggregated
        if (entry[8] === true) {
          return false;
        }

        return true;
      });

      sources[mappingEntry.source] = [...sources[mappingEntry.source], ...filteredPostData];
    });

    const aggData = compileAggregateData();

    if (aggData && aggData.length) {
      aggData.forEach(entry => {
        // Position 8 SourceAccount on Aggregate entries
        const sourceAccount = entry[8];
        if (sourceAccount) {
          sources[sourceAccount].push(entry);
        }
      });
    }

    modal.open('confirmDealPost', {
      size: 'md',
      style: 'center',
      title: 'Confirm Deal Post',
      staticBackdrop: true,
      hidePrint: true,
      hideFooter: true,
      message: (
        <PostDealConfirmation
          postingSource={postingSource}
          postingData={postingData}
          postingBranch={postingBranch}
          postingAddendum={postingAddendum}
          sources={sources}
          ready={ready}
          notready={notready}
          deal={deal}
          callback={() => fetchDeal({ clearCache: true })}
          initialPostingDate={titleDatesEqual ? titleDate : undefined}
        />
      ),
    });
  };

  const handleBook = quoteId => {
    return ApiService.post(`crm/quote/${quoteId}/book`).then(fetchDeal);
  };

  const handleSelectQuote = (event, quoteId) => {
    event.stopPropagation();

    if (event.target.checked) {
      setSelected([...selected, quoteId]);
    } else {
      setSelected(selected.filter(s => s !== quoteId));
    }
  };

  const getGlAccount = accountNum => {
    const record = accounts.find(a => a.id === accountNum);
    if (!record) {
      return accountNum;
    }

    return `${accountNum}: ${record.value}`;
  };

  const handleMappingChange = (event, quoteId) => {
    const mappingEntry = accountMappings.find(o => o.id === Number(event.target.value));

    if (mappingEntry) {
      setPostingSource({
        ...postingSource,
        [quoteId]: String(mappingEntry.source),
      });
    }

    setMappingValues({
      ...mappingValues,
      [quoteId]: Number(event.target.value),
    });
  };

  const setDefaultMappingValues = reset => {
    const defaultMappingValues = {};
    const defaultPostingSource = {};
    quotesThatAreReady().forEach(quote => {
      // Don't change what's there
      if (!reset) {
        if (mappingValues[quote.id]) {
          defaultMappingValues[quote.id] = mappingValues[quote.id];
        }

        if (postingSource[quote.id]) {
          defaultPostingSource[quote.id] = postingSource[quote.id];
        }
      }

      const vehicle = vehicles.find(v => v.itemid === quote.vehicleId);

      if (vehicle) {
        let found;

        // First check given gl codes
        for (const option of accountMappings) {
          if (option.glcode) {
            const glCodeValues = option.glcode.split('~!~');
            for (const glCodeValue of glCodeValues) {
              if (vehicle[glCodeFieldName] === glCodeValue) {
                found = option;
              }
            }
          }
        }

        // Then vehicle type & condition
        if (!found) {
          const condition = vehicle['New-Used'] === 'New' ? 'N' : 'U';
          const type = vehicle.Type;
          found = accountMappings.find(o => o.condition === condition && o.type === type);
        }

        if (found && !mappingValues[quote.id]) {
          if (!defaultMappingValues[quote.id]) {
            defaultMappingValues[quote.id] = found.id;
          }

          if (!defaultPostingSource[quote.id]) {
            defaultPostingSource[quote.id] = String(found.source);
          }
        }
      }
    });

    setMappingValues(defaultMappingValues);
    setPostingSource(defaultPostingSource);
  };

  const checkCosts = () => {
    setCheckingCost(true);
    return ApiService.put(`/crm/deal/${dealId}/update-cost`).then(response => {
      if (Object.keys(response.data).length) {
        const currentQuotes = [...quoteList];
        fetchDeal();

        modal.open('costChanges', {
          hidePrint: true,
          size: 'sm',
          style: 'center',
          title: 'Costs Updated',
          staticBackdrop: true,
          message: (
            <div>
              {Object.keys(response.data).map(quoteId => {
                const quoteChanges = response.data[quoteId];
                const quoteCostChange = quoteChanges['data.costsToDate'];
                const quote = currentQuotes.find(q => q.id === Number(quoteId));
                return (
                  <div>
                    <SimpleQuoteBadge quote={quote} />{' '}
                    <strong>
                      <CurrencyItem>{quoteCostChange.oldValue}</CurrencyItem>
                    </strong>{' '}
                    to{' '}
                    <strong>
                      <CurrencyItem>{quoteCostChange.newValue}</CurrencyItem>
                    </strong>
                  </div>
                );
              })}
            </div>
          ),
        });
      }
      setCheckingCost(false);
    });
  };

  const fetchDeal = () => {
    return ApiService.get(`crm/deal/${dealId}`).then(response => {
      const { quotes, ...restOfDeal } = response.data;
      setDeal(restOfDeal);
      setQuoteList(quotes);

      setPostingBranch(restOfDeal.branchId);

      refreshDeal(); // TODO Not optimal, used to update background information Deal Info
    });
  };

  const fetchMappings = () => {
    return ApiService.get(`crm/mappings?branchId=${String(postingBranch)}`).then(response => {
      const { mappings, mappedFields, accountsUsed } = response.data;
      setAccountMappings(mappings || []);
      setFieldLookup(mappedFields);
      setAccounts(accountsUsed);
    });
  };

  const fetchDealVehicles = () => {
    return ApiService.get(`crm/deal/${dealId}/vehicles`).then(response => {
      setVehicles(response.data);
    });
  };

  const compileData = () => {
    const dataMap = {};
    const aggregateMap = {};
    const runningTotal = {};
    dataMap.aggregate = aggregateMap;

    quotesThatAreReady().forEach(quote => {
      const data = [];
      runningTotal[quote.id] = 0;
      dataMap[quote.id] = data;
      quote.quoteId = quote.id;
      const mappingEntry = accountMappings.find(o => o.id === mappingValues[quote.id]);

      if (!mappingEntry) {
        return;
      }

      const mappingData = mappingEntry.mapping;

      if (!mappingData || !mappingData.field.length) {
        return;
      }

      mappingData.field.forEach((fieldName, index) => {
        const source = mappingEntry.source;
        const isDebit = mappingData.creditDebit[index] === 'D';
        const isAggregate =
          mappingData.aggregate && mappingData.aggregate[index] && mappingData.aggregate[index] === 'Y';

        // Check For Misc Fees or the like
        if (/:/.test(fieldName)) {
          const [lineItemFieldName, lineItemFieldNameValue, lineItemAmountFieldName] = fieldName.split(':');
          const lineItemField = fieldLookup.find(f => f.name === fieldName);
          // Find row in table

          if (!_.get(quote, lineItemFieldName)) {
            console.warn(`Invalid fieldname ${lineItemFieldName}`);
            return;
          }

          if (!_.get(quote, lineItemAmountFieldName)) {
            console.warn(`Invalid fieldname ${lineItemAmountFieldName}`);
            return;
          }

          const lineItems = _.get(quote, lineItemFieldName);
          const lineItemValues = _.get(quote, lineItemAmountFieldName);

          let fieldValue;

          // Write-Ins
          if (String(lineItemFieldNameValue) === '0') {
            let writeInTotal = 0;
            lineItems.forEach((lineItem, lineItemIndex) => {
              if (!/\d+/.test(lineItem)) {
                writeInTotal += Math.round(lineItemValues[lineItemIndex] * 100);
              }
            });
            fieldValue = writeInTotal / 100;
          } else {
            const valueIndex = lineItems.findIndex(e => String(e) === String(lineItemFieldNameValue));
            fieldValue = Number(lineItemValues[valueIndex] || 0);
          }

          const amount = !isDebit && fieldValue !== 0 ? fieldValue * -1 : fieldValue;

          const label = lineItemField.longLabel || lineItemField.label;

          // Zeros don't need to be posted, clutters books, crts
          if (!excludeZeros || (excludeZeros && amount !== 0)) {
            data.push([
              lineItemFieldName,
              label,
              getGlAccount(mappingData.account[index]),
              quote[mappingData.control[index]],
              dealId,
              quote.vehicleId,
              amount,
              isDebit,
              isAggregate,
            ]);
            runningTotal[quote.id] += Math.round(amount * 100);
          }
          return;
        }

        const field = fieldLookup.find(f => f.name === fieldName);

        if (field) {
          if (field.insideTable) {
            const fieldValue = _.get(quote, fieldName);
            const controlValues = _.get(quote, field.accountingControl, []);

            // Aggregate values based on control number
            if (fieldName === 'data.commission.amount') {
              const commissionAddendum = []; // Used to update the unit record
              const controlValueLookup = {};
              controlValues.forEach((controlValue, controlIndex) => {
                if (!controlValueLookup[controlValue]) {
                  controlValueLookup[controlValue] = [];
                }

                controlValueLookup[controlValue].push(controlIndex);
              });

              Object.keys(controlValueLookup).forEach(controlValue => {
                const valueIndices = controlValueLookup[controlValue];
                let controlTotal = 0;
                valueIndices.forEach(valueIndex => {
                  const amount =
                    !isDebit && Number(fieldValue[valueIndex]) !== 0
                      ? Number(fieldValue[valueIndex]) * -1
                      : Number(fieldValue[valueIndex]);
                  controlTotal += Math.round(amount * 100);
                });

                const postAmount = controlTotal / 100;

                data.push([
                  fieldName,
                  field.label,
                  getGlAccount(mappingData.account[index]),
                  controlValue,
                  dealId,
                  quote.vehicleId,
                  postAmount,
                  isDebit,
                  isAggregate,
                ]);
                runningTotal[quote.id] += Math.round(controlTotal);

                // Move the sign back if it was a Credit for the addendum
                const realAmount = !isDebit && postAmount !== 0 ? postAmount * -1 : postAmount;
                commissionAddendum.push({ userId: controlValue, amount: realAmount });
              });
              setPostingAddendum(c => {
                const currentQuote = c[quote.id] || {};
                return {
                  ...c,
                  [quote.id]: { ...currentQuote, commissionAddendum },
                };
              });
            } else {
              if (fieldValue && fieldValue.length) {
                fieldValue.forEach((value, tableIndex) => {
                  const amount = !isDebit && Number(value) !== 0 ? Number(value) * -1 : Number(value);
                  const controlValue = controlValues ? controlValues[tableIndex] : quote[mappingData.control[index]];
                  const label = field.longLabel || field.label;

                  // Zeros don't need to be posted, clutters books, crts
                  if (!excludeZeros || (excludeZeros && amount !== 0)) {
                    data.push([
                      fieldName,
                      label,
                      getGlAccount(mappingData.account[index]),
                      controlValue,
                      dealId,
                      quote.vehicleId,
                      amount,
                      isDebit,
                      isAggregate,
                    ]);
                    runningTotal[quote.id] += Math.round(amount * 100);
                  }
                });
              }
            }
          } else {
            const fieldValue = Number(_.get(quote, fieldName) || 0);
            const amount = !isDebit && fieldValue !== 0 ? fieldValue * -1 : fieldValue;

            if (isAggregate) {
              // Only Aggregate values if the posting account, field, and debit/credit settings match
              const aggKey = `${mappingData.field[index]}:${isDebit ? 'D' : 'C'}:${
                mappingData.account[index]
              }:${source}:${quote[mappingData.control[index]]}`;
              aggregateMap[aggKey] = (Math.round((aggregateMap[aggKey] || 0) * 100) + Math.round(amount * 100)) / 100;
            }

            const label = field.longLabel || field.label;

            // Zeros don't need to be posted, clutters books, crts
            if (!excludeZeros || (excludeZeros && amount !== 0)) {
              data.push([
                fieldName,
                label,
                getGlAccount(mappingData.account[index]),
                quote[mappingData.control[index]],
                dealId,
                quote.vehicleId,
                amount,
                isDebit,
                isAggregate,
              ]);
              runningTotal[quote.id] += Math.round(amount * 100);
            }
          }
        }
      });
    });

    setPostingData(dataMap);
    setPostingTotal(runningTotal);
  };

  const compileAggregateData = forGrid => {
    const aggregateData = postingData.aggregate;
    const compiledData = [];

    if (!Object.keys(aggregateData).length) {
      return compiledData;
    }

    Object.keys(aggregateData).forEach(key => {
      const [mappingFieldName, mappingDebitCredit, mappingAccount, sourceAccount, controlNumber] = key.split(':');
      const isDebit = mappingDebitCredit === 'D';
      const amount = aggregateData[key];
      const field = fieldLookup.find(f => f.name === mappingFieldName);

      // Zeros don't need to be posted, clutters books, crts
      if (!excludeZeros || (excludeZeros && amount !== 0)) {
        if (forGrid) {
          compiledData.push([
            mappingFieldName,
            field.label,
            getGlAccount(mappingAccount),
            sourceAccount,
            controlNumber,
            amount,
            isDebit,
          ]);
        } else {
          compiledData.push([
            mappingFieldName,
            field.label,
            getGlAccount(mappingAccount),
            controlNumber,
            dealId,
            'Aggregation',
            amount,
            isDebit,
            sourceAccount,
          ]);
        }
      }
    });

    return compiledData;
  };

  const renderBranchSelection = () => {
    return (
      <FormGroup className="form-select mb-0 mr-1 ml-2 d-inline-block">
        <FormControl onChange={handleCurrentBranchChange} as="select" value={postingBranch}>
          {user.branches.map(branch => (
            <option key={branch.id} value={branch.id}>
              {branch.name}
            </option>
          ))}
        </FormControl>
      </FormGroup>
    );
  };

  const renderStatusIcon = quoteId => {
    if (mappingValues[quoteId] !== '' && postingTotal[quoteId] !== 0) {
      return (
        <div className="d-inline-block ml-1 warning" title="Entries Unbalanced">
          <i
            className="fas fa-exclamation-circle fa-fw"
            style={{
              color: 'var(--red-5)',
            }}
          />
        </div>
      );
    }

    if (!mappingValues[quoteId]) {
      return (
        <div className="d-inline-block ml-1 warning" title="Select a Mapping">
          <i
            className="fad fa-exclamation-triangle fa-fw"
            style={{
              '--fa-primary-color': '#000',
              '--fa-secondary-color': 'var(--yellow-6)',
            }}
          />
        </div>
      );
    }

    if (!postingSource[quoteId]) {
      return (
        <div className="d-inline-block ml-1 warning" title="Select a Posting Source">
          <i
            className="fad fa-exclamation-triangle fa-fw"
            style={{
              '--fa-primary-color': '#000',
              '--fa-secondary-color': 'var(--yellow-6)',
            }}
          />
        </div>
      );
    }

    return (
      <div className="d-inline-block ml-1 warning" title="Looks Good">
        <i
          className="fas fa-check fa-fw"
          style={{
            color: 'var(--green-5)',
          }}
        />
      </div>
    );
  };

  const renderReadyList = () => {
    const quoteRender = quotesThatAreReady().map((quote, index) => {
      const vehicle = vehicles.find(v => v.itemid === quote.vehicleId);
      const odd = index % 2 === 0;
      const sourceKey = postingSource[quote.id] ? String(postingSource[quote.id]) : String(quote.id);

      const isBalanced = mappingValues[quote.id] && postingTotal[quote.id] === 0;

      return (
        <QuotePostLine
          key={quote.id}
          className={odd ? 'odd' : 'even'}
          quoteId={quote.id}
          onExpand={handleExpand}
          expanded={expandedQuotes.includes(quote.id)}
        >
          <QuotePostLinePrimary>
            <div className="quote-cd" style={{ minWidth: '65px' }}>
              <i
                className="fas fa-circle mr-1"
                style={{ color: quote.QuoteState.color }}
                title={quote.QuoteState.name}
              />
              Q{quote.id}
            </div>
            <div className="flex-grow-1">
              <div className="quote-cd">{quote.vehicleId}</div>
              {vehicle === undefined ? (
                <div className="quote-cd">Invalid Vehicle</div>
              ) : (
                <div className="quote-cd">
                  {vehicle['New-Used']} {vehicle.Type}
                </div>
              )}
              <div
                className="quote-cd dollar-value"
                style={{
                  minWidth: '100px',
                  textAlign: 'right',
                  // backgroundColor: 'var(--green-0)',
                }}
              >
                <CurrencyItem>{quote.data.grossProfit}</CurrencyItem>
              </div>
              <div
                className="quote-cd dollar-value"
                style={{
                  minWidth: '100px',
                  textAlign: 'right',
                  borderLeft: '0px',
                  // backgroundColor: '#eee',
                }}
              >
                <CurrencyItem>{quote.data.dueAtDelivery}</CurrencyItem>
              </div>
            </div>
            <div className="form-edit-container mr-1">
              <div className="control-wrapper">
                <FormInputListSearch
                  key={sourceKey}
                  controlId="posting-source"
                  onChange={(f, v) => handlePostingSourceChange(v, quote.id)}
                  search={{ page: 'AMF3' }}
                  field={{ label: '' }}
                  onSuggestionSelected={(suggestion, value) => handlePostingSourceSelected(value, quote.id)}
                  value={postingSource[quote.id]}
                  placeholder="Posting Source"
                />
              </div>
            </div>
            <div className="d-inline-block form-select">
              <FormControl
                onChange={evt => {
                  handleMappingChange(evt, quote.id);
                }}
                as="select"
                value={mappingValues[quote.id]}
              >
                <option value=""></option>
                {accountMappings.map(o => (
                  <option key={o.id} value={o.id}>
                    {o.title || `${o.condition === 'N' ? 'New' : 'Used'} ${o.type}`}
                  </option>
                ))}
              </FormControl>
            </div>
            {renderStatusIcon(quote.id)}
          </QuotePostLinePrimary>
          <QuotePostLineSecondary>
            {isBalanced === false && (
              <Alert className="p-2 mt-2" variant="danger">
                <i className="fas fa-exclamation-circle mr-1" /> Entries are unbalanced! [{postingTotal[quote.id] / 100}
                ]
              </Alert>
            )}
            <ModalTable
              controlId="post-deal-quote"
              headers={headers}
              data={postingData[quote.id]}
              hideToolbar
              style={{ height: 'initial' }}
              emptyMessage="Select a Mapping"
            />
          </QuotePostLineSecondary>
        </QuotePostLine>
      );
    });

    if (!quotesThatAreReady().length) {
      return null;
    }

    return (
      <div className="quote-section">
        <div className="quote-section-heading">
          <div className="quote-header">
            <Button variant="secondary" onClick={handleExpandAll} title="Expand/Collapse All">
              <i className="fas fa-chevron-double-down" />
            </Button>
          </div>
          <div className="right-toolbar">
            <Button
              className={classNames('primary-btn mr-1', { checked: !excludeZeros, unchecked: excludeZeros })}
              size="md"
              variant="secondary"
              onClick={() => setExcludeZeros(!excludeZeros)}
            >
              $0.00
              {excludeZeros ? (
                <i className="fa-solid fa-circle-xmark uncheck" />
              ) : (
                <i className="fas fa-check-circle check" />
              )}
            </Button>
            <div className="separator" />
            {renderBranchSelection()}
            <Button className="ml-1" variant="info" onClick={handlePostAll} disabled={checkingCost}>
              Post All
            </Button>
          </div>
        </div>
        <div className="quote-columns">
          <div className="quote-column gp">GP</div>
          <div className="quote-column due">Due</div>
        </div>
        <div className="quote-list">
          {quoteRender}
          {renderAggregateValues()}
        </div>
      </div>
    );
  };

  const renderNotReadyList = () => {
    const quoteRender = quotesThatAreNotReady().map(quote => {
      const hasError = notReadyErrors[quote.id] && notReadyErrors[quote.id].length > 0;
      const vehicle = vehicles.find(v => v.itemid === quote.vehicleId) || {};
      const status = getStatus(vehicle.Status);

      return (
        <div className="quote-line-wrapper" key={quote.id}>
          <div className="quote-line">
            <FormCheck>
              <FormCheck.Input
                type="checkbox"
                onChange={evt => handleSelectQuote(evt, quote.id)}
                checked={selected.includes(quote.id)}
              />
            </FormCheck>
            <div className="quote-cd" style={{ width: '75px' }}>
              <i
                className="fas fa-circle mr-1"
                style={{ color: quote.QuoteState.color }}
                title={quote.QuoteState.name}
              />
              Q{quote.id}
            </div>
            <div className="flex-grow-1">
              <Badge
                className="mr-1"
                title={vehicle?.Status}
                style={{ color: '#fff', padding: '3x 5px', backgroundColor: statusColors[status] }}
              >
                {status}
              </Badge>
              {quote.vehicleId} {vehicle.Make} {vehicle.Model}
            </div>
            {hasError === true && (
              <div className="error">
                {notReadyErrors[quote.id].map((message, index) => {
                  return <span key={`${quote.id}-${index}`}>{message}</span>;
                })}
                <div className="d-inline-block ml-1 warning" title={notReadyErrors[quote.id]}>
                  <i
                    className="fas fa-exclamation-circle fa-fw"
                    style={{
                      color: 'var(--red-5)',
                    }}
                  />
                </div>
              </div>
            )}
            <div className="quote-toolbar" />
          </div>
        </div>
      );
    });

    if (!quotesThatAreNotReady().length) {
      return null;
    }

    return (
      <div className="quote-section">
        <div className="quote-section-heading">
          <div className="quote-header">{renderStatusMessage()}</div>
          <div>
            {selected.length !== 0 && (
              <Button variant="seconary" onClick={handleBookSelected} disabled={booking}>
                Book Selected
              </Button>
            )}
            <Button className="ml-1" variant="success" onClick={handleBookAll} disabled={booking}>
              Book All
            </Button>
          </div>
        </div>
        <div className={classNames('quote-list', { booking })}>
          <div className="busy" />
          {quoteRender}
        </div>
      </div>
    );
  };

  const renderAlreadyPosted = () => {
    const quoteRender = quotesAlreadyPosted().map(quote => {
      const vehicle = vehicles.find(v => v.itemid === quote.vehicleId) || {};

      return (
        <QuotePostLine key={quote.id}>
          <QuotePostLinePrimary>
            <div className="quote-cd" style={{ width: '75px' }}>
              <i
                className="fas fa-circle mr-1"
                style={{ color: quote.QuoteState.color }}
                title={quote.QuoteState.name}
              />
              Q{quote.id}
            </div>
            <div className="quote-cd flex-grow-1">
              {quote.vehicleId} {vehicle.Make} {vehicle.Model}
            </div>
            <div>
              <Badge variant="info">Posted {moment(new Date(quote.postedDate)).fromNow()}</Badge>
            </div>
          </QuotePostLinePrimary>
        </QuotePostLine>
      );
    });

    if (!quotesAlreadyPosted().length) {
      return null;
    }

    return (
      <div className="quote-section">
        <div className="quote-section-heading">
          <div className="quote-header"></div>
          <div>
            {user.username === 'dsisupport' && (
              <Button className="ml-1" variant="success" onClick={handleRevertPosted}>
                Revert All
              </Button>
            )}
            <Button className="ml-1" variant="primary" onClick={handleCloseAll}>
              Close All
            </Button>
          </div>
        </div>
        <div className="quote-list">{quoteRender}</div>
      </div>
    );
  };

  const renderAggregateValues = () => {
    const aggData = compileAggregateData(true);

    if (!aggData.length) {
      return null;
    }

    return (
      <QuotePostLine
        key="agg"
        className="even"
        quoteId="agg"
        onExpand={handleExpand}
        expanded={expandedQuotes.includes('agg')}
      >
        <QuotePostLinePrimary>
          <div className="pt-1 pb-1"><strong>Aggregated Entries</strong></div>
        </QuotePostLinePrimary>

        <QuotePostLineSecondary>
          <ModalTable
            controlId="post-deal-aggregate"
            headers={aggHeaders}
            data={aggData}
            hideToolbar
            style={{ maxHeight: '250px', height: 'initial' }}
          />
        </QuotePostLineSecondary>
      </QuotePostLine>
    );
  };

  const renderStatusMessage = () => {
    const noun = `quote${selected.length > 1 ? 's' : ''}`;
    if (booking) {
      const message = selected.length ? `Booking ${selected.length} ${noun}` : 'Booking all quotes';
      return (
        <div>
          <i className="fad fa-spinner-third fa-spin" /> {message}
        </div>
      );
    } else {
      if (selected.length) {
        return (
          <div>
            <div
              className="hover-icon mr-1"
              title="Clear Selection"
              onClick={() => {
                setSelected([]);
              }}
              style={{ cursor: 'pointer' }}
            >
              <i className="fal fa-times" />
            </div>
            {selected.length} {noun} selected
          </div>
        );
      }
    }
  };

  const changeTab = tabKey => {
    if (String(tabKey) !== activeTabIndex) {
      setActiveTabIndex(String(tabKey));
    }
  };

  useEffect(() => {
    compileData();
  }, [mappingValues, quoteList, excludeZeros]);

  useEffect(() => {
    setDefaultMappingValues();
  }, [quoteList, accountMappings]);

  useEffect(() => {
    setDefaultMappingValues(true);
  }, [accountMappings]);

  useEffect(() => {
    setDefaultMappingValues();
  }, [vehicles]);

  useEffect(() => {
    fetchMappings();
  }, [postingBranch]);

  useEffect(() => {
    if (!loading) {
      checkCosts();
    }
  }, [loading]);

  useEffect(() => {
    Promise.all([fetchDeal(), fetchMappings(), fetchDealVehicles()]).then(() => {
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (activeTabIndex === 'notready' && !quotesThatAreNotReady().length) {
      if (quotesThatAreReady().length) {
        changeTab('ready');
      } else if (quotesAlreadyPosted().length) {
        changeTab('done');
      }
    }

    if (activeTabIndex === 'ready' && !quotesThatAreReady().length) {
      if (quotesThatAreNotReady().length) {
        changeTab('notready');
      } else if (quotesAlreadyPosted().length) {
        changeTab('done');
      }
    }

    if (activeTabIndex === 'done' && !quotesAlreadyPosted().length) {
      if (quotesThatAreNotReady().length) {
        changeTab('notready');
      } else if (quotesThatAreReady().length) {
        changeTab('ready');
      }
    }
  });

  if (loading) {
    return <LoadingPage message="Fetching Deal" className="mt-5 mb-5" />;
  }

  return (
    <div className="post-deal">
      <div className="deal-tab-container">
        <Tabs
          className="deal-tabs pl-3"
          defaultActiveKey={activeTabIndex}
          activeKey={activeTabIndex}
          onSelect={changeTab}
        >
          {quotesThatAreNotReady().length > 0 && (
            <Tab
              eventKey="notready"
              title={
                <div className="tab-header">
                  <div className="tab-label">
                    <i className="fas fa-exclamation-circle mr-1" style={{ color: 'var(--blue-5)' }} />
                    Not Ready
                  </div>
                  <Badge pill variant="danger ml-1">
                    {quotesThatAreNotReady().length}
                  </Badge>
                </div>
              }
            >
              {renderNotReadyList()}
            </Tab>
          )}
          {quotesThatAreReady().length > 0 && (
            <Tab
              eventKey="ready"
              title={
                <div className="tab-header">
                  <div className="tab-label">
                    <i className="fas fa-badge-dollar mr-1" style={{ color: 'var(--green-5)' }} />
                    Ready
                  </div>

                  <Badge pill variant="danger ml-1">
                    {quotesThatAreReady().length}
                  </Badge>
                </div>
              }
            >
              {renderReadyList()}
            </Tab>
          )}
          {quotesAlreadyPosted().length > 0 && (
            <Tab
              eventKey="done"
              title={
                <div className="tab-header">
                  <div className="tab-label">
                    <i className="fas fa-badge-check mr-1" style={{ color: 'var(--dpurple-5)' }} />
                    Posted
                  </div>
                  <Badge pill variant="danger ml-1">
                    {quotesAlreadyPosted().length}
                  </Badge>
                </div>
              }
            >
              {renderAlreadyPosted()}
            </Tab>
          )}
        </Tabs>
      </div>
    </div>
  );
};

export default PostDealToAccounting;
